import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
} from "react";
import { Navigate } from "react-router-dom";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "../../../../style/css/custom.css";
import Loader from "../../../../formHelper/Loader";
import Table from "../../../../formHelper/list";
import dayjs from "dayjs";

interface NotificationRow {
  id: string;
  type?: string;
  notifiable_type?: string;
  notifiable_id?: number | string | null;
  entity_type?: string | null;
  entity_id?: number | string | null;
  message?: string | null;
  meta?: any;
  user_name?: string | null;
  read_at?: string | null;
  created_at?: string | null;
  [k: string]: any;
}

interface EntityType {
  id: string;
  name: string;
}

const useDebounced = <T,>(value: T, ms = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
};

const THEME_KEY = "dataTheme";

const NotificationPush: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<NotificationRow[]>([]);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 450);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [entityType, setEntityType] = useState<string | null>(null);
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [readFilter, setReadFilter] = useState<"all" | "read" | "unread">(
    "all"
  );

  const isMobile = useIsMobile();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(
    () => new Set()
  );

  const fetchEntityTypes = useCallback(async () => {
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.getNotificationEntityTypes
      );
      const raw = res?.data?.data;
      if (!raw) {
        setEntityTypes([]);
        return;
      }
      if (Array.isArray(raw)) {
        setEntityTypes(
          raw.map((r: any) => ({
            id: String(r.id ?? r.key ?? r.value),
            name: String(r.name ?? r.label ?? r),
          }))
        );
      } else if (typeof raw === "object") {
        setEntityTypes(
          Object.entries(raw).map(([k, v]) => ({
            id: String(k),
            name: String(v),
          }))
        );
      } else {
        setEntityTypes([{ id: String(raw), name: String(raw) }]);
      }
    } catch (err) {
      console.error("Failed to fetch entity types:", err);
      setEntityTypes([]);
    }
  }, [backendRoute]);

  const safeString = (v: string | null | undefined) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    if (s === "") return null;
    if (s.toLowerCase() === "nan") return null;
    return s;
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / (perPage || 1)));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalItems, perPage, totalPages]);

  const fetchList = useCallback(
    async ({
      page = 1,
      perPageArg = perPage,
      startDateOverride,
      endDateOverride,
      filterOverride,
      entityTypeOverride,
    }: {
      page?: number;
      perPageArg?: number;
      startDateOverride?: string | null;
      endDateOverride?: string | null;
      filterOverride?: "all" | "read" | "unread" | null;
      entityTypeOverride?: string | null;
    } = {}) => {
      setLoading(true);
      try {
        const s =
          startDateOverride !== undefined ? startDateOverride : startDate;
        const e = endDateOverride !== undefined ? endDateOverride : endDate;
        const filterUsed =
          filterOverride !== undefined && filterOverride !== null
            ? filterOverride
            : readFilter;
        const entityUsed =
          entityTypeOverride !== undefined ? entityTypeOverride : entityType;

        const payload: any = {
          page,
          per_page: perPageArg,
          start_date: safeString(s) || undefined,
          end_date: safeString(e) || undefined,
          search: debouncedQuery.trim() || undefined,
          filter: filterUsed === "all" ? undefined : filterUsed,
        };

        const candidate = safeString(entityUsed);
        if (candidate) payload.entity_type = candidate;

        const result = await apiRequestCall(
          "post",
          backendRoute.getNotificationsList,
          { data: payload }
        );

        const res = result?.data?.data;
        if (res) {
          const apiPage = Number(res.current_page || page || 1);
          const apiPer = Number(res.per_page || perPageArg || 1);
          const apiTotal = Number(res.total || 0);
          const apiTotalPages = Math.max(1, Math.ceil(apiTotal / apiPer));
          const finalPage = apiPage > apiTotalPages ? apiTotalPages : apiPage;

          setList(res.data || []);
          setCurrentPage(finalPage);
          setPerPage(apiPer);
          setTotalItems(apiTotal);
        } else {
          setList([]);
          setTotalItems(0);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        setList([]);
        setTotalItems(0);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    },
    [
      startDate,
      endDate,
      debouncedQuery,
      readFilter,
      entityType,
      backendRoute,
      perPage,
    ]
  );

  useEffect(() => {
    fetchList({ page: currentPage, perPageArg: perPage });
  }, [debouncedQuery, currentPage, perPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchList({ page: 1, perPageArg: perPage });
  }, [readFilter, entityType]);

  useEffect(() => {
    fetchEntityTypes();
  }, [fetchEntityTypes]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {(currentPage - 1) * perPage + i + 1}
          </div>
        ),
      },
      { key: "entity_type", label: "Entity Type" },
      { key: "message", label: "Message" },
      {
        key: "meta",
        label: "Meta",
        render: (row: NotificationRow) => {
          if (!row.meta) return "-";
          const bits: string[] = [];
          if (row.meta.quotation_no) bits.push(String(row.meta.quotation_no));
          if (row.meta.order_no) bits.push(String(row.meta.order_no));
          if (row.meta.partial_order_no)
            bits.push(String(row.meta.partial_order_no));
          if (row.meta.amount)
            bits.push(`₹${Number(row.meta.amount).toLocaleString()}`);
          return bits.join(" | ") || JSON.stringify(row.meta);
        },
      },
      { key: "user_name", label: "Username" },
      {
        key: "read_at",
        label: "Read At",
        render: (r: NotificationRow) => {
          const time = r.read_at
            ? dayjs(r.read_at).format("DD-MM-YYYY HH:mm")
            : null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {r.read_at ? (
                <>
                  <span className="badge bg-soft-success">Read</span>
                  <small style={{ color: "#666" }}>{time}</small>
                </>
              ) : (
                <>
                  <span className="badge bg-soft-danger">Unread</span>
                  <small style={{ color: "#666" }}>—</small>
                </>
              )}
            </div>
          );
        },
      },
      { key: "created_at", label: "Created at" },
    ],
    [currentPage, perPage]
  );

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  const renderMetaBadges = (meta: any) => {
    if (!meta) return null;
    const known = [
      "quotation_no",
      "order_no",
      "partial_order_no",
      "amount",
      "note",
    ];
    return (
      <div className="mt-2 meta-block">
        <div className="d-flex flex-wrap gap-2">
          {meta.quotation_no && (
            <span className="badge bg-light border">
              Q#: {meta.quotation_no}
            </span>
          )}
          {meta.order_no && (
            <span className="badge bg-light border">
              Order: {meta.order_no}
            </span>
          )}
          {meta.partial_order_no && (
            <span className="badge bg-light border">
              Partial: {meta.partial_order_no}
            </span>
          )}
          {meta.amount && (
            <span className="badge bg-soft-success">
              ₹{Number(meta.amount).toLocaleString()}
            </span>
          )}

          {typeof meta === "object" &&
            Object.keys(meta)
              .filter((k) => known.indexOf(k) === -1)
              .map((k) => (
                <span key={k} className="badge bg-light border">
                  {k}: {String(meta[k])}
                </span>
              ))}
        </div>

        {meta.note && <div className="small text-muted mt-1">{meta.note}</div>}
      </div>
    );
  };

  const toggleExpand = (id: string | number) => {
    setExpandedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatAmount = (amt: any) =>
    typeof amt === "number" || !isNaN(Number(amt))
      ? `₹${Number(amt).toLocaleString()}`
      : String(amt);

  const renderMobileList = () => (
    <div className="notification-mobile-card">
      {list.length === 0 && !loading ? (
        <div className="text-center p-3">No notifications</div>
      ) : (
        list.map((r, idx) => {
          const id = r.id ?? idx;
          const expanded = expandedIds.has(id);
          const statusClass = r.read_at ? "nm-read" : "nm-unread";
          return (
            <article
              key={String(id)}
              className={`nm-card mb-2 ${
                expanded ? "nm-expanded" : ""
              } ${statusClass}`}
              role="article"
              aria-expanded={expanded}
            >
              <div className="nm-stripe" aria-hidden />
              <div className="nm-body p-2 d-flex gap-2">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div
                        className="nm-message"
                        title={String(r.message || "")}
                      >
                        {r.message || "-"}
                      </div>

                      <div className="nm-meta-row mt-1">
                        {r.user_name && (
                          <small
                            className="nm-chip"
                            title={`User: ${r.user_name}`}
                          >
                            {r.user_name}
                          </small>
                        )}
                        {r.entity_type && (
                          <small
                            className="nm-chip"
                            title={`Entity: ${r.entity_type}`}
                          >
                            {r.entity_type}
                          </small>
                        )}
                        {r.meta?.amount && (
                          <small className="nm-chip nm-amount" title={`Amount`}>
                            {formatAmount(r.meta.amount)}
                          </small>
                        )}
                        {r.meta?.quotation_no && (
                          <small className="nm-chip">
                            Q#: {r.meta.quotation_no}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="text-end nm-right">
                      <div>
                        {r.read_at ? (
                          <span className="badge bg-soft-success nm-status">
                            Read
                          </span>
                        ) : (
                          <span className="badge bg-soft-danger nm-status">
                            Unread
                          </span>
                        )}
                      </div>
                      <div className="nm-time small mt-1">
                        {r.created_at
                          ? dayjs(r.created_at).format("DD-MM-YYYY HH:mm")
                          : "-"}
                      </div>
                    </div>
                  </div>

                  {expanded && (
                    <div className="nm-expanded-body mt-2">
                      {renderMetaBadges(r.meta)}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="nm-toggle"
                aria-label={expanded ? "Collapse" : "Expand"}
                onClick={() => toggleExpand(id)}
              >
                <i
                  className={`ti ${
                    expanded ? "ti-chevron-up" : "ti-chevron-down"
                  }`}
                />
              </button>
            </article>
          );
        })
      )}
    </div>
  );

  const FiltersCompact: React.FC = () => (
    <div className="filters-compact mb-2">
      <div className="d-flex gap-2">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Search
            placeholder="Search notifications"
            value={query}
            onChange={(v: string) => {
              setQuery(v);
              setCurrentPage(1);
            }}
          />
        </div>

        <button
          className="btn btn-outline-primary btn-filter-icon"
          type="button"
          onClick={() => setMobileFiltersOpen((s) => !s)}
          aria-expanded={mobileFiltersOpen}
          aria-controls="mobileFiltersPanel"
          title="Toggle filters"
        >
          <i className="ti ti-filter" style={{ fontSize: 14 }} />
        </button>
      </div>

      {mobileFiltersOpen && (
        <div id="mobileFiltersPanel" className="card card-body p-2 mt-2">
          <div className="mb-2">
            <label className="form-label mb-1">Date Range</label>
            <DateRangePicker
              initialSettings={{
                autoUpdateInput: false,
                locale: { cancelLabel: "Clear" },
              }}
              onApply={(event: any, picker: any) => {
                const s = picker.startDate.format("YYYY-MM-DD");
                const e = picker.endDate.format("YYYY-MM-DD");
                setStartDate(s);
                setEndDate(e);
                setCurrentPage(1);
                fetchList({
                  page: 1,
                  perPageArg: perPage,
                  startDateOverride: s,
                  endDateOverride: e,
                  filterOverride: readFilter,
                  entityTypeOverride: entityType,
                });
              }}
              onCancel={() => {
                setStartDate("");
                setEndDate("");
                setCurrentPage(1);
                fetchList({
                  page: 1,
                  perPageArg: perPage,
                  startDateOverride: "",
                  endDateOverride: "",
                  filterOverride: readFilter,
                  entityTypeOverride: entityType,
                });
              }}
            >
              <input
                type="text"
                className="form-control"
                placeholder="Select Date Range"
                value={
                  startDate && endDate
                    ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                        endDate
                      ).format("DD-MM-YYYY")}`
                    : ""
                }
                readOnly
                style={{ cursor: "pointer" }}
              />
            </DateRangePicker>
          </div>

          <div className="mb-2">
            <label className="form-label mb-1">Entity Type</label>
            <select
              className="form-select"
              value={entityType ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                const final = v === "" ? null : v;
                setEntityType(final);
                setCurrentPage(1);
                fetchList({
                  page: 1,
                  perPageArg: perPage,
                  filterOverride: readFilter,
                  entityTypeOverride: final,
                });
              }}
            >
              <option value="">All</option>
              {entityTypes.map((et) => (
                <option key={et.id} value={et.id}>
                  {et.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <label className="d-block mb-1">Read Status</label>
            <div className="btn-group" role="group" aria-label="read-filter">
              <button
                type="button"
                className={`btn btn-sm ${
                  readFilter === "all" ? "btn-primary" : "btn-light"
                }`}
                onClick={() => {
                  setReadFilter("all");
                  setCurrentPage(1);
                  fetchList({
                    page: 1,
                    perPageArg: perPage,
                    filterOverride: "all",
                    entityTypeOverride: entityType,
                  });
                }}
              >
                All
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  readFilter === "unread" ? "btn-primary" : "btn-light"
                }`}
                onClick={() => {
                  setReadFilter("unread");
                  setCurrentPage(1);
                  fetchList({
                    page: 1,
                    perPageArg: perPage,
                    filterOverride: "unread",
                    entityTypeOverride: entityType,
                  });
                }}
              >
                Unread
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  readFilter === "read" ? "btn-primary" : "btn-light"
                }`}
                onClick={() => {
                  setReadFilter("read");
                  setCurrentPage(1);
                  fetchList({
                    page: 1,
                    perPageArg: perPage,
                    filterOverride: "read",
                    entityTypeOverride: entityType,
                  });
                }}
              >
                Read
              </button>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-light btn-sm w-100"
              onClick={() => {
                setEntityType(null);
                setReadFilter("all");
                setCurrentPage(1);
                fetchList({
                  page: 1,
                  perPageArg: perPage,
                  filterOverride: "all",
                  entityTypeOverride: null,
                });
              }}
            >
              Reset
            </button>
            <button
              className="btn btn-primary btn-sm w-100"
              onClick={() => fetchList({ page: 1, perPageArg: perPage })}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Notifications
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <Search
                  placeholder="Search notifications"
                  value={query}
                  onChange={(v: string) => {
                    setQuery(v);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-12 col-md-3">
                <div className="icon-form" style={{ display: "flex", gap: 8 }}>
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>

                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onApply={(event: any, picker: any) => {
                      const s = picker.startDate.format("YYYY-MM-DD");
                      const e = picker.endDate.format("YYYY-MM-DD");
                      setStartDate(s);
                      setEndDate(e);
                      setCurrentPage(1);
                      fetchList({
                        page: 1,
                        perPageArg: perPage,
                        startDateOverride: s,
                        endDateOverride: e,
                        filterOverride: readFilter,
                        entityTypeOverride: entityType,
                      });
                    }}
                    onCallback={(start: any, end: any) => {
                      const s = start.format("YYYY-MM-DD");
                      const e = end.format("YYYY-MM-DD");
                      setStartDate(s);
                      setEndDate(e);
                      setCurrentPage(1);
                      fetchList({
                        page: 1,
                        perPageArg: perPage,
                        startDateOverride: s,
                        endDateOverride: e,
                        filterOverride: readFilter,
                        entityTypeOverride: entityType,
                      });
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                      fetchList({
                        page: 1,
                        perPageArg: perPage,
                        startDateOverride: "",
                        endDateOverride: "",
                        filterOverride: readFilter,
                        entityTypeOverride: entityType,
                      });
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="form-sorts dropdown">
                  <a
                    href="#"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={(e) => e.preventDefault()}
                  >
                    <i className="ti ti-filter-share" /> Filter
                  </a>

                  <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                    <div className="filter-set-view">
                      <div className="mb-2">
                        <label className="form-label mb-1">Entity Type</label>
                        <select
                          className="form-select"
                          value={entityType ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const final = v === "" ? null : v;
                            setEntityType(final);
                            setCurrentPage(1);
                            fetchList({
                              page: 1,
                              perPageArg: perPage,
                              filterOverride: readFilter,
                              entityTypeOverride: final,
                            });
                          }}
                        >
                          <option value="">All</option>
                          {entityTypes.map((et) => (
                            <option key={et.id} value={et.id}>
                              {et.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <label className="d-block mb-1">Read Status</label>
                        <div
                          className="btn-group"
                          role="group"
                          aria-label="read-filter"
                        >
                          <button
                            type="button"
                            className={`btn ${
                              readFilter === "all" ? "btn-primary" : "btn-light"
                            }`}
                            onClick={() => {
                              setReadFilter("all");
                              setCurrentPage(1);
                              fetchList({
                                page: 1,
                                perPageArg: perPage,
                                filterOverride: "all",
                                entityTypeOverride: entityType,
                              });
                            }}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className={`btn ${
                              readFilter === "unread"
                                ? "btn-primary"
                                : "btn-light"
                            }`}
                            onClick={() => {
                              setReadFilter("unread");
                              setCurrentPage(1);
                              fetchList({
                                page: 1,
                                perPageArg: perPage,
                                filterOverride: "unread",
                                entityTypeOverride: entityType,
                              });
                            }}
                          >
                            Unread
                          </button>
                          <button
                            type="button"
                            className={`btn ${
                              readFilter === "read"
                                ? "btn-primary"
                                : "btn-light"
                            }`}
                            onClick={() => {
                              setReadFilter("read");
                              setCurrentPage(1);
                              fetchList({
                                page: 1,
                                perPageArg: perPage,
                                filterOverride: "read",
                                entityTypeOverride: entityType,
                              });
                            }}
                          >
                            Read
                          </button>
                        </div>
                      </div>

                      <div className="filter-reset-btns row mt-3">
                        <div className="col-6">
                          <button
                            className="btn btn-light w-100"
                            onClick={() => {
                              setEntityType(null);
                              setReadFilter("all");
                              setCurrentPage(1);
                              fetchList({
                                page: 1,
                                perPageArg: perPage,
                                filterOverride: "all",
                                entityTypeOverride: null,
                              });
                            }}
                          >
                            Reset
                          </button>
                        </div>
                        <div className="col-6">
                          <button
                            className="btn btn-primary w-100"
                            onClick={() =>
                              fetchList({ page: 1, perPageArg: perPage })
                            }
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <Loader />
              ) : isMobile ? (
                renderMobileList()
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <Table data={list} columns={columns} />
                </div>
              )}
              <div className="card-footer">
                <Pagination
                  currentPage={currentPage}
                  perPage={perPage}
                  totalItems={totalItems}
                  onPageChange={(p) => {
                    setCurrentPage(p);
                  }}
                  onPerPageChange={(pp) => {
                    setPerPage(pp);
                    setCurrentPage(1);
                    fetchList({
                      page: 1,
                      perPageArg: pp,
                      filterOverride: readFilter,
                      entityTypeOverride: entityType,
                    });
                  }}
                  perPageOptions={[
                    { value: 5, label: "5" },
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* defaults (light) */
        :root {
          --page-bg: #f3f4f6;
          --card-bg: #ffffff;
          --card-border: rgba(15,23,42,0.06);
          --card-shadow: 0 1px 3px rgba(2,6,23,0.06);
          --text: #0f1724;
          --muted: #6b7280;
          --chip-bg: rgba(15,23,42,0.04);
          --chip-border: rgba(15,23,42,0.06);
          --chip-color: #0f1724;
          --amount-bg: rgba(16,185,129,0.07);
          --amount-color: #047857;
          --stripe-read: #16a34a;
          --stripe-unread: #ef4444;
        }

        /* ensure explicit light theme overrides when body.light-theme exists */
        body.light-theme {
          --page-bg: #f3f4f6;
          --card-bg: #ffffff; /* pure white cards in light mode */
          --card-border: rgba(15,23,42,0.06);
          --card-shadow: 0 1px 3px rgba(2,6,23,0.06);
          --text: #0f1724;
          --muted: #6b7280;
          --chip-bg: rgba(15,23,42,0.04);
          --chip-border: rgba(15,23,42,0.06);
          --chip-color: #0f1724;
          --amount-bg: rgba(16,185,129,0.07);
          --amount-color: #047857;
          --stripe-read: #16a34a;
          --stripe-unread: #ef4444;
        }

        /* dark theme overrides */
        body.dark-theme {
          --page-bg: #071122;
          --card-bg: #071826;
          --card-border: rgba(255,255,255,0.04);
          --card-shadow: 0 1px 3px rgba(0,0,0,0.35);
          --text: #e6eef8;
          --muted: #9ca3af;
          --chip-bg: rgba(255,255,255,0.03);
          --chip-border: rgba(255,255,255,0.06);
          --chip-color: #e6eef8;
          --amount-bg: rgba(16,185,129,0.12);
          --amount-color: #86efac;
          --stripe-read: #16a34a;
          --stripe-unread: #f87171;
        }

        /* Mobile notification card (compact & tappable) */
        .notification-mobile-card { padding: 6px 8px; background: transparent; }

        .nm-card {
          position: relative;
          display: flex;
          border-radius: 10px;
          overflow: hidden;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          box-shadow: var(--card-shadow);
          transition: transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;
          color: var(--text);
        }
        .nm-card:active { transform: translateY(1px); }

        .nm-stripe { width: 6px; background: var(--stripe-unread); }
        .nm-read .nm-stripe { background: var(--stripe-read); }
        .nm-unread .nm-stripe { background: var(--stripe-unread); }

        .nm-body { width: 100%; display: flex; align-items: flex-start; }

        .nm-message {
          font-weight: 600;
          font-size: 14px;
          line-height: 1.25;
          color: var(--text);
          word-break: break-word;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .nm-meta-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 6px; }
        .nm-chip {
          display: inline-block;
          padding: 3px 8px;
          font-size: 12px;
          border-radius: 999px;
          background: var(--chip-bg);
          border: 1px solid var(--chip-border);
          color: var(--chip-color);
        }
        .nm-chip.nm-amount {
          background: var(--amount-bg);
          border-color: rgba(16,185,129,0.14);
          color: var(--amount-color);
        }

        .nm-right { min-width: 80px; max-width: 110px; flex-shrink: 0; text-align: right; }
        .nm-status { font-size: 11px; padding: 3px 6px; border-radius: 8px; }
        .nm-time { color: var(--muted); font-size: 12px; }

        .nm-expanded .nm-body { padding-bottom: 0; }
        .nm-expanded-body { padding: 8px 10px 12px 10px; border-top: 1px dashed rgba(0,0,0,0.04); }

        .nm-toggle {
          position: absolute;
          right: 8px;
          bottom: 8px;
          background: transparent;
          border: 0;
          padding: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--muted);
          border-radius: 6px;
        }

        @media (max-width: 380px) {
          .nm-right { min-width: 64px; max-width: 88px; }
          .nm-message { font-size: 13px; -webkit-line-clamp: 3; }
        }

        /* compact filter icon styles kept */
        .filters-compact .form-control { flex: 1 1 auto; min-width: 0; }
        .btn-filter-icon {
          width: 36px;
          height: 36px;
          padding: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
        }
        @media (max-width: 575px) {
          .page-title { font-size: 1rem; }
        }
      `}</style>
    </div>
  );  
};

export default NotificationPush;
